/* Ozalo — scripts du header (méga-menu, recherche avec suggestions, compteur du panier) */
if (!customElements.get('olv-mega-controller')) {
  customElements.define('olv-mega-controller', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const categories = Array.from(this.querySelectorAll('[data-olvm-category]'));
      const mobileToggle = this.querySelector('[data-olvm-toggle]');
      const isDesktop = () => this.classList.contains('olvm-desktop');
      const closeCategories = (except) => categories.forEach((item) => { if (item !== except) item.open = false; });
      categories.forEach((item) => {
        item.querySelector('summary').addEventListener('click', () => {
          clearTimeout(this.closeTimer);
          closeCategories(item);
        }, options);
        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse' || !isDesktop()) return;
          clearTimeout(this.closeTimer);
          this.openTimer = setTimeout(() => { closeCategories(item); item.open = true; }, 80);
        }, options);
        item.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse' || !isDesktop()) return;
          clearTimeout(this.openTimer);
          this.closeTimer = setTimeout(() => { if (!item.contains(document.activeElement)) item.open = false; }, 180);
        }, options);
      });
      if (mobileToggle) {
        mobileToggle.addEventListener('toggle', () => {
          document.documentElement.style.overflow = mobileToggle.open ? 'hidden' : '';
        }, options);
      }
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const detail = event.target.closest('details[open]');
        if (detail && this.contains(detail)) {
          detail.open = false;
          detail.querySelector('summary').focus();
          event.preventDefault();
          event.stopPropagation();
        }
      }, options);
      this.addEventListener('focusout', () => {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          if (!this.contains(document.activeElement) && isDesktop()) closeCategories();
        }, 0);
      }, options);
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) {
          closeCategories();
          if (mobileToggle) mobileToggle.open = false;
        }
      }, options);
      window.matchMedia('(min-width: 750px)').addEventListener('change', () => {
        closeCategories();
        if (mobileToggle) mobileToggle.open = false;
      }, options);
    }
    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.closeTimer);
      clearTimeout(this.openTimer);
      clearTimeout(this.focusTimer);
      document.documentElement.style.overflow = '';
    }
  });
}

if (!customElements.get('olv-predictive-search')) {
  customElements.define('olv-predictive-search', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('[data-olv-ps-panel]');
      this.status = this.querySelector('[data-olv-ps-status]');

      /* Validation : « casques » devient « casque » ; si une catégorie porte exactement ce nom,
         on ouvre directement la collection au lieu de la page de résultats */
      if (this.form && this.input) {
        this.form.addEventListener('submit', async (event) => {
          const query = this.normalize(this.input.value);
          this.input.value = query;
          if (this.dataset.redirect !== 'true' || query.length < 2 || this.redirecting) return;
          event.preventDefault();
          this.redirecting = true;
          let target = null;
          try {
            target = this.bestCollection(query, await this.fetchCollections(query));
          } catch (error) {
            target = null;
          }
          if (target) {
            window.location.href = target.url;
          } else {
            this.redirecting = false;
            this.form.submit();
          }
        }, options);
      }
      if (!this.input || !this.panel) return;

      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 200);
      }, options);
      this.input.addEventListener('focus', () => {
        if (this.panel.childElementCount && this.input.value.trim().length >= 2) this.open();
      }, options);
      this.addEventListener('keydown', (event) => this.onKeydown(event), options);
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      }, options);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.request?.abort();
      clearTimeout(this.timer);
    }

    normalize(value) {
      return value
        .trim()
        .split(/\s+/)
        .map((word) => (word.length > 4 && /[sx]$/i.test(word) && !/ss$/i.test(word) ? word.slice(0, -1) : word))
        .join(' ');
    }

    /* Minuscules sans accents : « Écran » = « ecran » */
    fold(value) {
      return String(value || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    }

    words(value) {
      return this.fold(value).split(/[^a-z0-9]+/).filter(Boolean);
    }

    /* Chaque mot tapé doit être le début d'un mot du texte (« casque » trouve « Casques ») */
    matches(text, query) {
      const words = this.words(text);
      const terms = this.words(query);
      return terms.length > 0 && terms.every((term) => words.some((word) => word.startsWith(term)));
    }

    /* Collection dont le nom est exactement le mot tapé, au singulier près */
    bestCollection(query, collections) {
      const key = this.words(this.normalize(query)).join(' ');
      return collections.find((collection) => this.words(this.normalize(collection.title)).join(' ') === key) || null;
    }

    async fetchCollections(query) {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'collection',
        'resources[limit]': '10',
        'resources[options][prefix]': 'last'
      });
      const response = await fetch(`${this.dataset.suggestUrl}.json?${params}`);
      if (!response.ok) throw new Error(response.status);
      const data = await response.json();
      return data.resources?.results?.collections || [];
    }

    async search() {
      const typed = this.input.value.trim();
      if (typed.length < 2) {
        this.close();
        this.panel.replaceChildren();
        return;
      }
      const query = this.normalize(typed);
      this.request?.abort();
      this.request = new AbortController();
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection',
        'resources[limit]': '10',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][prefix]': 'last',
        'resources[options][fields]': this.dataset.fields || 'title'
      });
      try {
        const response = await fetch(`${this.dataset.suggestUrl}.json?${params}`, { signal: this.request.signal });
        if (!response.ok) throw new Error(response.status);
        const data = await response.json();
        if (this.input.value.trim() !== typed) return;
        this.render(typed, query, data.resources?.results || {});
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    render(typed, query, results) {
      /* Shopify ajoute des produits « proches » (recherche sémantique) : on ne garde que ceux
         dont le titre ou le type contient vraiment le mot tapé */
      const products = (results.products || [])
        .filter((product) => this.matches(`${product.title} ${product.type || product.product_type || ''}`, query))
        .slice(0, 6);
      const collections = (results.collections || []).filter((collection) => this.matches(collection.title, query));
      const make = (tag, className, text) => {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (text != null) node.textContent = text;
        return node;
      };
      const currency = window.Shopify?.currency?.active || 'EUR';
      let formatter;
      try {
        formatter = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency });
      } catch (e) {
        formatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
      }
      const withWidth = (src, width) => src + (src.includes('?') ? '&' : '?') + 'width=' + width;
      const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const terms = query.split(/\s+/).filter((t) => t.length > 1).map(escapeRegExp);
      const highlight = (target, text) => {
        if (!terms.length) { target.textContent = text; return; }
        const pattern = new RegExp(`(${terms.join('|')})`, 'gi');
        text.split(pattern).forEach((part, index) => {
          target.append(index % 2 ? make('mark', null, part) : document.createTextNode(part));
        });
      };

      this.panel.replaceChildren();
      this.panel.classList.toggle('olv-ps--has-cats', collections.length > 0);

      /* Colonne de gauche : catégories */
      if (collections.length) {
        const catCol = make('div', 'olv-ps__col olv-ps__col--cats');
        catCol.append(make('p', 'olv-ps__heading', this.dataset.collectionsLabel));
        const cats = make('ul', 'olv-ps__cats');
        collections.slice(0, 6).forEach((collection) => {
          const item = make('li');
          const link = make('a', 'olv-ps__cat', collection.title);
          link.href = collection.url;
          item.append(link);
          cats.append(item);
        });
        catCol.append(cats);
        this.panel.append(catCol);
      }

      /* Colonne de droite : produits */
      const productCol = make('div', 'olv-ps__col olv-ps__col--products');
      productCol.append(make('p', 'olv-ps__heading', this.dataset.productsLabel));

      if (products.length) {
        const list = make('ul', 'olv-ps__list');
        products.forEach((product) => {
          const item = make('li');
          const link = make('a', 'olv-ps__item');
          link.href = product.url;

          const media = make('span', 'olv-ps__media');
          const imageSrc = product.featured_image?.url || product.image;
          if (imageSrc) {
            const image = document.createElement('img');
            image.src = withWidth(imageSrc, 144);
            image.srcset = `${withWidth(imageSrc, 144)} 1x, ${withWidth(imageSrc, 288)} 2x`;
            image.alt = '';
            image.width = 72;
            image.height = 72;
            image.loading = 'lazy';
            media.append(image);
          }

          const text = make('span', 'olv-ps__text');
          const type = product.type || product.product_type;
          if (type) text.append(make('span', 'olv-ps__type', type.toLowerCase()));
          const title = make('span', 'olv-ps__title');
          highlight(title, product.title);
          text.append(title);

          const pricing = make('span', 'olv-ps__pricing');
          const prices = make('span', 'olv-ps__prices');
          const current = parseFloat(product.price);
          const compare = parseFloat(product.compare_at_price_max || product.compare_at_price_min || 0);
          const onSale = !Number.isNaN(current) && compare > current;
          if (onSale) prices.append(make('s', 'olv-ps__compare', formatter.format(compare)));
          if (!Number.isNaN(current)) prices.append(make('span', 'olv-ps__price', formatter.format(current)));
          pricing.append(prices);
          if (onSale) {
            const percent = Math.round((1 - current / compare) * 100);
            if (percent > 0) pricing.append(make('span', 'olv-ps__badge', `-${percent}%`));
          }

          link.append(media, text, pricing);
          item.append(link);
          list.append(item);
        });
        productCol.append(list);
      } else {
        productCol.append(make('p', 'olv-ps__empty', `${this.dataset.noResults} « ${typed} »`));
      }

      const all = make('a', 'olv-ps__all', this.dataset.viewAll);
      all.href = `${this.dataset.searchUrl}?${new URLSearchParams({ q: query, type: 'product', 'options[prefix]': 'last', 'options[fields]': this.dataset.fields || 'title' })}`;
      productCol.append(all);
      this.panel.append(productCol);

      if (this.status) {
        this.status.textContent = products.length
          ? `${products.length} produit(s) suggéré(s)`
          : `${this.dataset.noResults} « ${typed} »`;
      }
      this.open();
    }

    links() {
      return Array.from(this.panel.querySelectorAll('a'));
    }

    onKeydown(event) {
      if (this.panel.hidden && event.key !== 'ArrowDown') return;
      const links = this.links();
      const index = links.indexOf(document.activeElement);
      if (event.key === 'Escape') {
        this.close();
        this.input.focus();
        event.preventDefault();
      } else if (event.key === 'ArrowDown') {
        if (this.panel.hidden && this.panel.childElementCount) this.open();
        if (!links.length) return;
        links[index < links.length - 1 ? index + 1 : 0].focus();
        event.preventDefault();
      } else if (event.key === 'ArrowUp' && index !== -1) {
        if (index === 0) this.input.focus();
        else links[index - 1].focus();
        event.preventDefault();
      }
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (!this.panel) return;
      this.panel.hidden = true;
      this.input?.setAttribute('aria-expanded', 'false');
    }
  });
}

if (!window.olvCartCountBound) {
  window.olvCartCountBound = true;
  const setCount = (count) => {
    if (typeof count !== 'number' || Number.isNaN(count)) return;
    document.querySelectorAll('[data-olv-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-olv-cart-sr]').forEach((el) => { el.textContent = count + ' article(s)'; });
  };
  document.addEventListener('cart:update', (event) => {
    const detail = event.detail || {};
    const count = detail.data?.itemCount ?? detail.resource?.item_count;
    if (typeof count === 'number') {
      setCount(count);
    } else {
      fetch((window.Shopify?.routes?.root || '/') + 'cart.js')
        .then((r) => r.json())
        .then((c) => setCount(c.item_count))
        .catch(() => {});
    }
  });
}
